// Loan Goals tab (Vendor Program v2 PR 1) — TanStack Query hooks over
// loanGoalsApi. Mirrors the useQaReviews conventions (stable key factory;
// mutations invalidate the overview; toasts/errors live in the components).

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { loanGoalsApi } from "@/api/portfolioAdmin/loanGoalsApi";
import type {
  CreateLoanGoalRequest,
  LoanGoalActivityDto,
  LoanGoalsOverviewDto,
  UpdateLoanGoalRequest,
} from "@/api/portfolioAdmin/loanGoalsApi";
import { financialReportingKeys } from "./useFinancialReporting";
import { invalidateLoanHealth } from "./invalidation";

export function loanGoalsKey(loanId: string) {
  return ["portfolio-admin", "loan", loanId, "goals"] as const;
}

export function loanGoalActivityKey(goalId: string) {
  return ["portfolio-admin", "loan-goal", goalId, "activity"] as const;
}

export function useLoanGoalsOverview(loanId: string | undefined) {
  return useQuery<LoanGoalsOverviewDto>({
    queryKey: loanGoalsKey(loanId ?? ""),
    queryFn: () => loanGoalsApi.overview(loanId!),
    enabled: !!loanId,
  });
}

export function useLoanGoalActivity(goalId: string | undefined) {
  return useQuery<LoanGoalActivityDto[]>({
    queryKey: loanGoalActivityKey(goalId ?? ""),
    queryFn: () => loanGoalsApi.activity(goalId!),
    enabled: !!goalId,
  });
}

function useGoalMutation<TArgs, TResult>(
  loanId: string,
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Return the promise so mutateAsync resolves AFTER the refetch — the
    // tab renders fresh assignment state before closing controls. Goals
    // feed the VG-M1 reporting card + the loan health Goals light too.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: loanGoalsKey(loanId) }),
        qc.invalidateQueries({
          queryKey: financialReportingKeys.loanOverview(loanId),
        }),
        invalidateLoanHealth(qc, loanId),
      ]),
  });
}

export function useCreateLoanGoal(loanId: string) {
  return useGoalMutation(loanId, (body: CreateLoanGoalRequest) =>
    loanGoalsApi.create(loanId, body),
  );
}

/** The synchronous AI propose run — the component owns the long-pending
 *  treatment (45–90s) and must never double-fire while pending. */
export function useDistillLoanGoals(loanId: string) {
  return useGoalMutation(loanId, (args: { vendorType: string | null }) =>
    loanGoalsApi.distill(loanId, args.vendorType),
  );
}

export function useUpdateLoanGoal(loanId: string) {
  return useGoalMutation(
    loanId,
    (args: { goalId: string; body: UpdateLoanGoalRequest }) =>
      loanGoalsApi.update(args.goalId, args.body),
  );
}

export function useApproveLoanGoal(loanId: string) {
  return useGoalMutation(loanId, (args: { goalId: string }) =>
    loanGoalsApi.approve(args.goalId),
  );
}

export function useAchieveLoanGoal(loanId: string) {
  return useGoalMutation(loanId, (args: { goalId: string }) =>
    loanGoalsApi.achieve(args.goalId),
  );
}

export function useRetireLoanGoal(loanId: string) {
  return useGoalMutation(loanId, (args: { goalId: string }) =>
    loanGoalsApi.retire(args.goalId),
  );
}

export function useDiscardLoanGoal(loanId: string) {
  return useGoalMutation(
    loanId,
    (args: { goalId: string; reason: string | null }) =>
      loanGoalsApi.discard(args.goalId, args.reason),
  );
}

export function useUndiscardLoanGoal(loanId: string) {
  return useGoalMutation(loanId, (args: { goalId: string }) =>
    loanGoalsApi.undiscard(args.goalId),
  );
}

export function useAssignLoanGoal(loanId: string) {
  return useGoalMutation(
    loanId,
    (args: { goalId: string; partyAssignmentId: string | null }) =>
      loanGoalsApi.assign(args.goalId, args.partyAssignmentId),
  );
}

export function useBulkAssignLoanGoals(loanId: string) {
  return useGoalMutation(
    loanId,
    (args: { goalIds: string[]; partyAssignmentId: string }) =>
      loanGoalsApi.bulkAssign(loanId, args.goalIds, args.partyAssignmentId),
  );
}
